import { get, put, post } from "@/shared/utils/fetchApi";
import type { AiModelTestReport } from "@/shared/types/aiModels";
import type { ModelPurpose } from "@/shared/utils/modelCatalog";

/**
 * Modelos de IA da PLATAFORMA (AB#85605).
 *
 * A escolha por finalidade — conversação, indexação e transcrição — dos provedores que
 * atendem os recursos da própria plataforma: preenchimento assistido, expansão de busca na
 * base de conhecimento e a transcrição das reuniões.
 *
 * O CADASTRO do provedor não passa por aqui: ele é feito pela tela de integrações de sempre,
 * escopada na organização da plataforma (`organization_id` devolvido por `getModels`).
 */

export interface PlatformModelChoice {
  integration_base_id: string;
  model: string;
  /** Extras da escolha — hoje só `prompt_context`, da transcrição. */
  options?: Record<string, unknown>;
}

export interface PlatformAiModels {
  /**
   * A organização que detém os provedores da plataforma. `null` num ambiente onde as
   * migrations ainda não rodaram — a tela avisa em vez de deixar salvar no vazio.
   */
  organization_id: string | null;
  conversational: PlatformModelChoice | null;
  embedding: PlatformModelChoice | null;
  transcription: PlatformModelChoice | null;
  /** Provedor pago da busca web, tentado depois do gratuito embutido (AB#86885). */
  web_search?: PlatformModelChoice | null;
  /**
   * A CADEIA de contingência de cada finalidade, na ordem de tentativa (AB#85617).
   *
   * Lista, e não um elo só: a prioridade sempre descreveu uma cadeia, e o gateway já
   * percorre quantos elos receber.
   */
  fallbacks: {
    conversational: PlatformModelChoice[];
    embedding: PlatformModelChoice[];
    transcription: PlatformModelChoice[];
    web_search?: PlatformModelChoice[];
  };
}

export type PlatformAiModelsPayload = Partial<
  Record<ModelPurpose, PlatformModelChoice | null>
>;

/** O payload da cadeia: a lista de cada finalidade, na ordem de tentativa (AB#85617). */
export type PlatformAiFallbackPayload = Partial<Record<ModelPurpose, PlatformModelChoice[]>>;

export class PlatformAiService {
  private static readonly BASE_URL = "/platform/ai-models";

  static async getModels(): Promise<PlatformAiModels> {
    return get<PlatformAiModels>(this.BASE_URL);
  }

  static async updateModels(payload: PlatformAiModelsPayload): Promise<PlatformAiModels> {
    return put<PlatformAiModels>(this.BASE_URL, payload);
  }

  /**
   * Grava a CONTINGÊNCIA, sem tocar nas escolhas principais (AB#85617).
   *
   * Rota própria e não uma bandeira no `updateModels`: a tela tem um botão de salvar por
   * bloco, e cada um grava só o seu nível.
   */
  static async updateFallback(payload: PlatformAiFallbackPayload): Promise<PlatformAiModels> {
    return put<PlatformAiModels>(`${this.BASE_URL}/fallback`, payload);
  }

  /**
   * Testa UMA finalidade com a escolha que está na tela, antes de gravar.
   *
   * 200 mesmo com o modelo reprovado: a reprovação é resultado do teste, não falha da
   * chamada — quem lê o relatório é a própria tela.
   */
  static async testModel(probe: {
    purpose: ModelPurpose;
    integration_base_id: string;
    model: string;
  }): Promise<AiModelTestReport> {
    return post<AiModelTestReport>(`${this.BASE_URL}/test`, probe);
  }
}
